import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { Distance, UserRaceRelation } from '@runner/shared';
import { apiFetch } from '../api/fetch';
import { useAuth } from '../auth/AuthContext';

/**
 * sessionStorage key holding a guest's bookmark intent. A guest who clicks
 * bookmark stores the raceId here before the OAuth redirect; after login the
 * provider applies it once and clears it. Survives the same-tab full redirect.
 */
export const PENDING_BOOKMARK_KEY = 'runner:pendingBookmarkRaceId';

export interface UserRaceItem {
  relation: UserRaceRelation;
  finishTimeSec: number | null;
  distance: Distance | null;
  note: string | null;
  createdAt: number;
  updatedAt: number;
  race: {
    id: string;
    name: string;
    nameLocal: string;
    date: string;
    region: string;
    lat: number;
    lng: number;
    status: 'open' | 'upcoming' | 'closed' | 'completed';
  };
}

export interface FinishInput {
  raceId: string;
  finishTimeSec: number;
  distance: Distance;
  note?: string;
}

export interface UserRacesState {
  loading: boolean;
  error: string | null;
  items: UserRaceItem[];
  bookmarkedIds: Set<string>;
  registeredIds: Set<string>;
  finishedIds: Set<string>;
  refresh: () => Promise<void>;
  addBookmark: (raceId: string) => Promise<void>;
  removeBookmark: (raceId: string) => Promise<void>;
  addRegistration: (raceId: string) => Promise<void>;
  addFinish: (input: FinishInput) => Promise<void>;
}

const UserRacesContext = createContext<UserRacesState | null>(null);

interface ApiListResponse {
  data: UserRaceItem[];
}

function addIdTo(set: Set<string>, id: string): Set<string> {
  if (set.has(id)) return set;
  const next = new Set(set);
  next.add(id);
  return next;
}

function removeIdFrom(set: Set<string>, id: string): Set<string> {
  if (!set.has(id)) return set;
  const next = new Set(set);
  next.delete(id);
  return next;
}

export function UserRacesProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [items, setItems] = useState<UserRaceItem[]>([]);
  const [bookmarkedIds, setBookmarkedIds] = useState<Set<string>>(new Set());
  const [registeredIds, setRegisteredIds] = useState<Set<string>>(new Set());
  const [finishedIds, setFinishedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const activeUserIdRef = useRef<string | null>(null);

  const applyItems = useCallback((list: UserRaceItem[]) => {
    setItems(list);
    const bm = new Set<string>();
    const rg = new Set<string>();
    const fn = new Set<string>();
    for (const entry of list) {
      if (entry.relation === 'bookmarked') bm.add(entry.race.id);
      if (entry.relation === 'registered') rg.add(entry.race.id);
      if (entry.relation === 'finished') fn.add(entry.race.id);
    }
    setBookmarkedIds(bm);
    setRegisteredIds(rg);
    setFinishedIds(fn);
  }, []);

  const clearAll = useCallback(() => {
    setItems([]);
    setBookmarkedIds(new Set());
    setRegisteredIds(new Set());
    setFinishedIds(new Set());
    setError(null);
  }, []);

  const refresh = useCallback(async () => {
    if (!user) {
      clearAll();
      return;
    }
    const userId = user.id;
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/users/me/races');
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const body = (await res.json()) as ApiListResponse;
      if (activeUserIdRef.current !== userId) return;
      applyItems(body.data);
    } catch (err) {
      if (activeUserIdRef.current !== userId) return;
      setError(err instanceof Error ? err.message : 'failed');
    } finally {
      if (activeUserIdRef.current === userId) setLoading(false);
    }
  }, [user, applyItems, clearAll]);

  useEffect(() => {
    activeUserIdRef.current = user?.id ?? null;
    if (!user) {
      clearAll();
      return;
    }
    void refresh();
  }, [user, refresh, clearAll]);

  const addBookmark = useCallback(async (raceId: string) => {
    setBookmarkedIds((prev) => addIdTo(prev, raceId));
    try {
      const res = await apiFetch('/api/users/me/races', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ raceId, relation: 'bookmarked' }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await refresh();
    } catch (err) {
      setBookmarkedIds((prev) => removeIdFrom(prev, raceId));
      throw err;
    }
  }, [refresh]);

  const removeBookmark = useCallback(async (raceId: string) => {
    setBookmarkedIds((prev) => removeIdFrom(prev, raceId));
    try {
      const res = await apiFetch(
        `/api/users/me/races/${encodeURIComponent(raceId)}?relation=bookmarked`,
        { method: 'DELETE' },
      );
      if (!res.ok && res.status !== 404) throw new Error(`HTTP ${res.status}`);
      await refresh();
    } catch (err) {
      setBookmarkedIds((prev) => addIdTo(prev, raceId));
      throw err;
    }
  }, [refresh]);

  const addRegistration = useCallback(async (raceId: string) => {
    setRegisteredIds((prev) => addIdTo(prev, raceId));
    try {
      const res = await apiFetch('/api/users/me/races', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ raceId, relation: 'registered' }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await refresh();
    } catch (err) {
      setRegisteredIds((prev) => removeIdFrom(prev, raceId));
      throw err;
    }
  }, [refresh]);

  const addFinish = useCallback(async (input: FinishInput) => {
    setFinishedIds((prev) => addIdTo(prev, input.raceId));
    try {
      const res = await apiFetch('/api/users/me/races', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          raceId: input.raceId,
          relation: 'finished',
          finishTimeSec: input.finishTimeSec,
          distance: input.distance,
          note: input.note,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await refresh();
    } catch (err) {
      setFinishedIds((prev) => removeIdFrom(prev, input.raceId));
      throw err;
    }
  }, [refresh]);

  // After a guest logs in (user null → defined), apply the bookmark they
  // intended before the OAuth redirect. addBookmark is an idempotent upsert, so
  // re-applying an already-bookmarked race is safe. removeItem first → once only.
  useEffect(() => {
    if (!user) return;
    const pending = sessionStorage.getItem(PENDING_BOOKMARK_KEY);
    if (!pending) return;
    sessionStorage.removeItem(PENDING_BOOKMARK_KEY);
    void addBookmark(pending);
  }, [user, addBookmark]);

  return (
    <UserRacesContext.Provider
      value={{
        loading,
        error,
        items,
        bookmarkedIds,
        registeredIds,
        finishedIds,
        refresh,
        addBookmark,
        removeBookmark,
        addRegistration,
        addFinish,
      }}
    >
      {children}
    </UserRacesContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useUserRaces(): UserRacesState {
  const ctx = useContext(UserRacesContext);
  if (!ctx) throw new Error('useUserRaces must be used within UserRacesProvider');
  return ctx;
}

export interface UseBookmarkResult {
  bookmarked: boolean;
  loading: boolean;
  toggle: () => Promise<void>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useBookmark(raceId: string): UseBookmarkResult {
  const { bookmarkedIds, addBookmark, removeBookmark } = useUserRaces();
  const [pending, setPending] = useState(false);
  const bookmarked = bookmarkedIds.has(raceId);

  const toggle = useCallback(async () => {
    if (pending) return;
    setPending(true);
    try {
      if (bookmarked) {
        await removeBookmark(raceId);
      } else {
        await addBookmark(raceId);
      }
    } catch {
      // optimistic update has already rolled back inside the provider.
    } finally {
      setPending(false);
    }
  }, [pending, bookmarked, raceId, addBookmark, removeBookmark]);

  return { bookmarked, loading: pending, toggle };
}
